import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import { useState } from 'react';

import {
  MugMockup,
  TshirtMockup,
  BoxMockup,
  CoffeeMockup,
  LogoController,
  TumblerMockup,
  ProductChips,
} from 'components/productMockups';

import { Footer } from 'components/studio';
import { NavLink } from 'react-router-dom';

/* =====================================================
   PRODUCT STUDIO PAGE
===================================================== */

export default function ProductStudioPage() {
  const logos = ['./og_image.png', './logo2.png', './logo3.png'];

  const [product, setProduct] = useState('tshirt');
  const [selectedLogo, setSelectedLogo] = useState(logos[0]);

  return (
    <div className='flex flex-col w-full h-screen bg-white'>
      <StudioHeader />

      <ProductChips product={product} setProduct={setProduct} />

      {/* MAIN LAYOUT */}

      <div className='flex flex-col flex-1 min-h-0 overflow-hidden lg:flex-row'>
        {/* DESKTOP SIDEBAR */}

        <LogoSidebar
          logos={logos}
          selectedLogo={selectedLogo}
          setSelectedLogo={setSelectedLogo}
        />

        {/* CANVAS AREA */}

        <div className='relative flex-1 w-full h-full min-h-0'>
          <Canvas
            camera={{ position: [0, 1.5, 5], fov: 50 }}
            shadows
            className='w-full h-full'
          >
            <color attach='background' args={['#ECE9E2']} />

            <Environment preset='studio' />

            <Scene product={product} logo={selectedLogo} />

            <OrbitControls
              enableZoom
              minDistance={3}
              maxDistance={8}
              maxPolarAngle={Math.PI / 2.1}
            />
          </Canvas>

          <LogoController product={product} />
        </div>

        {/* MOBILE LOGO SELECTOR */}

        <MobileLogoSelector
          logos={logos}
          selectedLogo={selectedLogo}
          setSelectedLogo={setSelectedLogo}
        />
      </div>

      {/* Desktop only footer */}

      <div className='lg:block'>
        <Footer />
      </div>
    </div>
  );
}

/* =====================================================
   HEADER
===================================================== */

function StudioHeader() {
  return (
    <header
      className='
        flex
        items-center
        justify-between
        h-14
        px-6
        border-b
        border-[#E7E2D9]
        bg-white
      '
    >
      <NavLink to='/' className='flex flex-col leading-tight'>
        <span className='text-[15px] font-semibold tracking-[0.08em] text-[#1F1F1F]'>
          Tomado Studio
        </span>

        <span className='text-[11px] text-[#8A867F]'>
          Product Mockup Studio
        </span>
      </NavLink>

      <span className='hidden sm:block text-xs text-[#8A867F]'>
        Preview your brand
      </span>
    </header>
  );
}

/* =====================================================
   SCENE SWITCH
===================================================== */

function Scene({ product, logo }) {
  switch (product) {
    case 'mug':
      return <MugMockup logo={logo} product={product} />;

    case 'coffee':
      return <CoffeeMockup logo={logo} product={product} />;

    case 'box':
      return <BoxMockup logo={logo} product={product} />;

    case 'tumbler':
      return <TumblerMockup logo={logo} product={product} />;

    default:
      return <TshirtMockup logo={logo} product={product} />;
  }
}

/* =====================================================
   DESKTOP SIDEBAR
===================================================== */

function LogoSidebar({ logos, selectedLogo, setSelectedLogo }) {
  return (
    <aside
      className='
        hidden
        lg:flex
        flex-col
        items-center
        gap-2
        py-5
        w-28
        border-r
        border-[#E7E2D9]
        bg-white
      '
    >
      <span className='text-[10px] text-[#8A867F] uppercase tracking-widest'>
        Logos
      </span>

      {logos.map((logo) => {
        const active = logo === selectedLogo;

        return (
          <button
            key={logo}
            onClick={() => setSelectedLogo(logo)}
            className={`
              w-20
              h-20
              rounded-lg
              border
              bg-white
              flex
              items-center
              justify-center
              transition
              shadow-sm
              overflow-hidden
              hover:scale-105
              ${
                active
                  ? `
                    border-[#C6A75E]
                    ring-1
                    ring-[#C6A75E]
                  `
                  : 'border-[#E7E2D9]'
              }
            `}
          >
            <img src={logo} alt='logo' className='object-cover w-full h-full' />
          </button>
        );
      })}
    </aside>
  );
}

/* =====================================================
   MOBILE LOGO SELECTOR
===================================================== */

function MobileLogoSelector({ logos, selectedLogo, setSelectedLogo }) {
  return (
    <div
      className='
        lg:hidden
        flex
        gap-3
        px-4
        py-3
        overflow-x-auto
        border-t
        border-[#E7E2D9]
        bg-white
      '
    >
      {logos.map((logo) => {
        const active = logo === selectedLogo;

        return (
          <button
            key={logo}
            onClick={() => setSelectedLogo(logo)}
            className={`
              w-16
              h-16
              flex-shrink-0
              rounded-lg
              border
              bg-white
              overflow-hidden
              transition
              ${
                active
                  ? `
                    border-[#C6A75E]
                    ring-1
                    ring-[#C6A75E]
                  `
                  : 'border-[#E7E2D9]'
              }
            `}
          >
            <img src={logo} alt='logo' className='object-cover w-full h-full' />
          </button>
        );
      })}
    </div>
  );
}
